import { createSelector } from "reselect"
import { get, first, last, chain, size, some, set } from "lodash"
import { getStartDate, getEndDate, getSelectedDate } from "selectors/dates"
import { getSelectedViews, getSelectedViewsFactors } from "selectors/factors"
import { getIsActive, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeFactorCategories } from "helpers/factors"
import { ATTRIBUTION_METHODS, computeAttributionMethod } from "helpers/risk"
import { getSegment } from "modules/analyze-header/selectors/segment"
import { NORMALIZATION, computeMCTEAdjustedSegment } from "helpers/segments"
import { getBasePositionSetWithSegment } from "selectors/base"

const defaultRiskData = {}
export const getRiskRaw = state => get(state, "global.data.analyzeRisk.data.model.simulation.risk", defaultRiskData)

export const getRisk = createSelector(
  [getRiskRaw, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification],
  (riskRaw, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) => chain(riskRaw)
    .map(item => {
      if (!item.total) {
        return null
      }
      let factors = item.attribution.factors
      if (mustAggregateFactors) {
        factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
      }

      if (size(selectedViews)) {
        factors = chain(item.attribution.factors)
          .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
          .map(factor => ({ ...factor, customView: true }))
          .concat(factors)
          .value()
      }

      return {
        date: item.date,
        total: item.total,
        factorsTotal: item.attribution.summary.factors,
        specific: item.attribution.summary.specific,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
      }
    })
    .filter(data => data)
    .value(),
)

export const getFilteredRisk = createSelector(
  [getRisk, getStartDate, getEndDate],
  (risk, startDate, endDate) =>
    risk.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getFilteredRisk],
  (risk) => first(risk),
)

export const getCurrent = createSelector(
  [getRisk],
  (risk) => last(risk),
)

export const getFirstRiskDate = createSelector(
  [getFilteredRisk],
  (risk) => get(first(risk), "date", null),
)

export const getLastRiskDate = createSelector(
  [getFilteredRisk],
  (risk) => get(last(risk), "date", null),
)

export const getRiskSelectedDate = createSelector(
  [getLastRiskDate, getSelectedDate],
  (lastRiskDate, selectedDate) => {
    if (selectedDate > lastRiskDate) {
      return lastRiskDate
    }
    return selectedDate
  },
)

export const getAttributionMethod = createSelector(
  [getIsActive],
  computeAttributionMethod,
)

export const getMCTEAdjustedSegment = createSelector(
  [getSegment, getAttributionMethod, getIsActive],
  computeMCTEAdjustedSegment,
)

export const getMCTEAdjustedBase = createSelector(
  [getBasePositionSetWithSegment, getAttributionMethod, getIsActive],
  (base, attributionMethod, isActive) => {
    if (isActive && attributionMethod === ATTRIBUTION_METHODS.MCTE && get(base, "segment")) {
      return set({ ...base }, "segment.normalization", NORMALIZATION.STANDALONE)
    }
    return base
  },
)
